import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class EncryptionService {
  // 32 bytes (256 bits) key for AES-256
  private readonly ENCRYPTION_KEY = 'ikshana-256bit-secret-key-1234567890';
  private readonly ALGORITHM = 'AES-GCM';
  private readonly IV_LENGTH = 12;

  constructor() {}

  async encrypt(data: any): Promise<string> {
    try {
      // Convert data to string
      const dataString = JSON.stringify(data);
      
      // Generate a random initialization vector
      const iv = crypto.getRandomValues(new Uint8Array(this.IV_LENGTH));
      
      // Convert the encryption key to a CryptoKey
      const key = await this.getCryptoKey();
      
      // Encrypt the data
      const encodedData = new TextEncoder().encode(dataString);
      const encryptedData = await crypto.subtle.encrypt(
        {
          name: this.ALGORITHM,
          iv: iv
        },
        key,
        encodedData
      );
      
      // Combine IV and encrypted data
      const combined = new Uint8Array(iv.length + encryptedData.byteLength);
      combined.set(iv);
      combined.set(new Uint8Array(encryptedData), iv.length);
      
      // Convert to base64 for storage
      return btoa(String.fromCharCode(...combined));
    } catch (error) {
      console.error('Encryption error:', error);
      throw error;
    }
  }

  async decrypt(encryptedData: string): Promise<any> {
    try {
      // Convert from base64
      const combined = Uint8Array.from(atob(encryptedData), c => c.charCodeAt(0));
      
      // Extract IV and encrypted data
      const iv = combined.slice(0, this.IV_LENGTH);
      const data = combined.slice(this.IV_LENGTH);
      
      // Get the crypto key
      const key = await this.getCryptoKey();
      
      // Decrypt the data
      const decryptedData = await crypto.subtle.decrypt(
        {
          name: this.ALGORITHM,
          iv: iv
        },
        key,
        data
      );
      
      // Convert back to string and parse JSON
      const decryptedString = new TextDecoder().decode(decryptedData);
      return JSON.parse(decryptedString);
    } catch (error) {
      console.error('Decryption error:', error);
      throw error;
    }
  }

  private async getCryptoKey(): Promise<CryptoKey> {
    // Convert the secret key to a byte array
    const keyData = new TextEncoder().encode(this.ENCRYPTION_KEY);
    
    // Hash the key to ensure it's exactly 32 bytes
    const hashBuffer = await crypto.subtle.digest('SHA-256', keyData);
    
    // Import the key with proper algorithm parameters
    return await crypto.subtle.importKey(
      'raw',
      hashBuffer,
      {
        name: this.ALGORITHM
      },
      false,
      ['encrypt', 'decrypt']
    );
  }
} 